import IconButton from "@/components/IconButton";
import MaterialIcon from "@/components/MaterialIcon";
import SkeletonTextLine from "@/components/SkeletonTextLine";
import { PersonalizedDiabetesProtocol } from "@/model/ClinicalProtocol";
import { DateTimeFormatter, ZoneId } from "@js-joda/core";
import { Locale } from "@js-joda/locale";
import { ComponentPropsWithoutRef, ReactNode } from "react";
import InfoBox from "../InfoBox";

type BloodGlucoseThresholdsBoxProps = {
  personalizedClinicalProtocol?: PersonalizedDiabetesProtocol;
  isLoading: boolean;
  onEditSelected: () => void;
} & ComponentPropsWithoutRef<"div">;

const DiabetesProtocolBox = ({
  personalizedClinicalProtocol,
  isLoading,
  onEditSelected,
  className,
  ...otherProps
}: BloodGlucoseThresholdsBoxProps) => {
  const { name, thresholds, lastUpdatedAt, lastUpdatedBy } = personalizedClinicalProtocol ?? {};
  return (
    <InfoBox className={className} {...otherProps}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <InfoBox.Header>Protocol - {name}</InfoBox.Header>
        <IconButton variant="neutral" onClick={onEditSelected} disabled={isLoading}>
          <MaterialIcon iconName="edit" style={{ fontSize: "20px" }} />
        </IconButton>
      </div>
      <div className="grid grid-cols-[auto_auto] gap-x-4">
        <div className="flex flex-col">
          <span className="mb-1 underline">Fasting</span>
          <InfoBox.Lines className="gap-x-2">
            <InfoBox.Line
              label="Emergency low:"
              truncate={false}
              value={<Value value={`≤ ${thresholds?.emergencyLow ?? 0}`} isLoading={isLoading} />}
            />
            <InfoBox.Line
              label="Critical low:"
              truncate={false}
              value={<Value isLoading={isLoading} value={`≤ ${thresholds?.criticalLow ?? 0}`} />}
            />
            <InfoBox.Line label="-" value={null} />
            <InfoBox.Line
              label="Elevated:"
              truncate={false}
              value={
                <Value isLoading={isLoading} value={`≥ ${thresholds?.fastingElevated ?? 0}`} />
              }
            />
            <InfoBox.Line
              label="Critical high:"
              truncate={false}
              value={<Value isLoading={isLoading} value={`≥ ${thresholds?.criticalHigh ?? 0}`} />}
            />
          </InfoBox.Lines>
        </div>
        <div className="flex flex-col">
          <span className="mb-1 underline">Postprandial</span>
          <InfoBox.Lines className="gap-x-2">
            <InfoBox.Line
              label="Emergency low:"
              truncate={false}
              value={<Value value={`≤ ${thresholds?.emergencyLow}`} isLoading={isLoading} />}
            />
            <InfoBox.Line
              label="Critical low:"
              truncate={false}
              value={<Value isLoading={isLoading} value={`≤ ${thresholds?.criticalLow ?? 0}`} />}
            />
            <InfoBox.Line
              label="Low:"
              truncate={false}
              value={
                <Value isLoading={isLoading} value={`≤ ${thresholds?.postprandialLow ?? 0}`} />
              }
            />
            <InfoBox.Line
              label="Elevated:"
              truncate={false}
              value={
                <Value isLoading={isLoading} value={`≥ ${thresholds?.postprandialElevated ?? 0}`} />
              }
            />
            <InfoBox.Line
              label="Critical high:"
              truncate={false}
              value={<Value isLoading={isLoading} value={`≥ ${thresholds?.criticalHigh ?? 0}`} />}
            />
          </InfoBox.Lines>
        </div>
      </div>
      {isLoading ? (
        <SkeletonTextLine className="mb-[3px] mt-[19px] h-[14px] w-48" />
      ) : (
        lastUpdatedAt && (
          <p className="mt-4 max-w-[452px] text-sm italic">
            {`Modified on ${dateFormatter.format(lastUpdatedAt.atZone(ZoneId.SYSTEM))}${lastUpdatedBy ? ` by ${lastUpdatedBy.displayName}` : ""}`}
          </p>
        )
      )}
    </InfoBox>
  );
};

const dateFormatter = DateTimeFormatter.ofPattern("M/d/yyyy").withLocale(Locale.US);

type ValueProps = {
  value?: ReactNode;
  isLoading: boolean;
};

const Value = ({ value, isLoading }: ValueProps) =>
  isLoading ? (
    <SkeletonTextLine className="mt-1 h-4 min-w-[5ch]" />
  ) : (
    <p className="overflow-visible text-nowrap">{value ?? "N/A"}</p>
  );

export default DiabetesProtocolBox;
